import _ from "lodash";
import update from "immutability-helper";
import client from "lib/apolloClient/client";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { generateRandomId } from "Utils";
import moment from "moment";

import {
  downloadFileOfStudentListByFamilyStatusMutation,
  familyInvitationForStudentMutation,
  uploadFamilyMembersDetailTemplateMutation,
  familyInviteForClassMutation,
  decideUserApprovalStatusMutation,
  confirmFamilyMemberUploadDetailsMutation,
  entityBulkUploadCompareDataMutation,
} from "./ManageFamilyMutations";
import { getStudentFeedQuery } from "StudentSetup/modules/StudentQueries";
import {
  getStudentFamilyDetails,
  writeStudentFamiliesInCache,
  getStudentFeedForOrganizationFromCache,
  writeStudentFeedForOrganizationToCache,
} from "StudentSetup/modules/GraphqlHelpers";

import { getStudentFamilyStatusInsightsQueryV2 } from "./ManageFamilyQuery";

import { removeChildForFamilyMemberMutation } from "StudentSetup/modules/StudentMutations";

import { colors } from "Constants";
import {
  ADMIN_SEARCH_TEXT_FIELDS,
  getUserCourseMapFilters,
} from "Administrator/modules/Utils";
import { ATTENDANCE_CONSTANTS } from "Constants/attendanceConstants";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";

export const NAME = "manageFamily";

export const INIT_FILTERS = "INIT_FILTERS" + " " + NAME;
export const UPDATE_FILTERS = "UPDATE_FILTERS" + " " + NAME;
export const INIT_FAMILY_INVITE_FILTERS =
  "INIT_FAMILY_INVITE_FILTERS" + " " + NAME;
export const UPDATE_FAMILY_INVITE_FILTERS =
  "UPDATE_FAMILY_INVITE_FILTERS" + " " + NAME;
export const SET_SELECTED_OR_EXCLUDED_STUDENTS =
  "SET_SELECTED_OR_EXCLUDED_STUDENTS" + " " + NAME;
export const SET_ARE_ALL_STUDENTS_SELECTED =
  "SET_ARE_ALL_STUDENTS_SELECTED" + " " + NAME;
export const SET_BULK_SELECTED_STUDENT_STATUS_COUNTS =
  "SET_BULK_SELECTED_STUDENT_STATUS_COUNTS" + " " + NAME;
export const RESET_BULK_ACTIONS_STATE = "RESET_BULK_ACTIONS_STATE" + " " + NAME;
export const SET_BULK_ACTIVITIES_POLLING_STATE =
  "SET_BULK_ACTIVITIES_POLLING_STATE" + " " + NAME;

export const getFamilyStatuses = ({ omits = [] } = {}) => {
  const options = [
    {
      label: "common:no_family_added",
      value: "UNCONNECTED",
      color: "var(--text-disabled)",
      tagColor: "var(--text-disabled)",
      order: 4,
    },
    {
      label: "common:invite_sent",
      value: "INVITED",
      color: "var(--text-semantic-info)",

      tagColor: "var(--interactive-tertiary)",
      order: 3,
    },
    {
      label: "common:connected",
      value: "ROSTERED",
      color: "var(--text-semantic-success)",
      tagColor: "var(--interactive-secondary)",
      order: 1,
    },
    {
      label: "common:approval_requested",
      value: "REQUESTED",
      color: "var(--text-semantic-warning)",
      tagColor: "var(--interactive-semantic-error)",
      order: 2,
    },
    {
      label: "common:invited_expired",
      value: "EXPIRED",
      color: colors.decorativeSurfaceFourD,
      tagColor: "var(--decorative-foreground-yellow)",
      order: 2,
    },
  ];
  return _.filter(options, option => !_.includes(omits, option.value));
};

const _getFamilyStatusFilterOptions = ({ t, includeAllOption = false }) => {
  const sortedOptions = _.sortBy(
    _.map(getFamilyStatuses({ omits: ["EXPIRED"] }), status => {
      const { label, value, order } = status;
      return {
        label: t(label),
        key: value,
        value,
        order,
      };
    }),
    "order"
  );
  if (includeAllOption) {
    return [
      { label: t("common:all"), key: "ALL", value: "ALL" },
      ...sortedOptions,
    ];
  }
  return sortedOptions;
};

export const getFamilyStatusFilterOptions = _.memoize(
  params => _getFamilyStatusFilterOptions(params),
  ({ includeAllOption = false }) => `includeAllOption:${!!includeAllOption}`
);

export const updateFilters = data => {
  return {
    type: UPDATE_FILTERS,
    data,
  };
};

export const initFilters = () => {
  return {
    type: INIT_FILTERS,
  };
};

export const updateFamilyInviteFilters = data => {
  return {
    type: UPDATE_FAMILY_INVITE_FILTERS,
    data,
  };
};

export const initFamilyInviteFilters = () => {
  return {
    type: INIT_FAMILY_INVITE_FILTERS,
  };
};

export const setSelectedOrExcludedStudents = data => {
  return {
    type: SET_SELECTED_OR_EXCLUDED_STUDENTS,
    payload: data,
  };
};

export const setAreAllStudentsSelected = value => ({
  type: SET_ARE_ALL_STUDENTS_SELECTED,
  payload: value,
});

export const setBulkSelectedStudentStatusCounts = data => {
  return {
    type: SET_BULK_SELECTED_STUDENT_STATUS_COUNTS,
    payload: data,
  };
};

export const resetBulkActionsState = () => {
  return {
    type: RESET_BULK_ACTIONS_STATE,
  };
};

export const setBulkActivitiesPollingState = isActive => ({
  type: SET_BULK_ACTIVITIES_POLLING_STATE,
  payload: { isActive },
});

const _getFamilyFeedQueryFilters = ({
  filters,
  loginActiveTab = "staff",
  courseId,
  curriculumProgramId,
  archivalState,
  academicYearId,
  blockStatus,
}) => {
  const {
    searchText = "",
    courses,
    familyStatus,
    adminFamilyStatus,
    lastLoggedIn,
  } = filters;
  const sendFilters = {};
  if (searchText) {
    sendFilters["searchText"] = searchText;
    sendFilters["searchTextFields"] = [
      ATTENDANCE_CONSTANTS.NAME,
      ATTENDANCE_CONSTANTS.SOURCED_ID,
      ATTENDANCE_CONSTANTS.FAMILY_MEMBER_NAME,
      ATTENDANCE_CONSTANTS.FAMILY_EMAIL,
    ];
  }

  if (loginActiveTab == "admin") {
    sendFilters["courses"] = !_.isEmpty(courses) ? courses : [];
    sendFilters["searchTextFields"] = [
      ...ADMIN_SEARCH_TEXT_FIELDS["family"],
      ATTENDANCE_CONSTANTS.FAMILY_MEMBER_NAME,
      ATTENDANCE_CONSTANTS.FAMILY_EMAIL,
    ];
    if (curriculumProgramId)
      sendFilters["curriculumProgramIds"] = [curriculumProgramId];
  } else {
    sendFilters["courses"] = !_.isEmpty(courseId) ? courseId : [];
  }

  const currentFamilyStatus =
    loginActiveTab == "admin" ? adminFamilyStatus : familyStatus;

  if (!_.isEmpty(currentFamilyStatus) && currentFamilyStatus !== "ALL") {
    sendFilters["familyStatus"] = currentFamilyStatus;
  }

  if (!_.isEmpty(lastLoggedIn)) {
    sendFilters["familyStatus"] = "ROSTERED";
    sendFilters["familyNotActiveSince"] = lastLoggedIn;
  }

  if (!_.isEmpty(archivalState)) {
    sendFilters["archivalState"] = archivalState;
  }
  if (!_.isEmpty(blockStatus)) {
    sendFilters["blockStatus"] = blockStatus;
  }
  sendFilters["academicYearId"] = academicYearId;

  return sendFilters;
};

const _getFamilyCountFilter = ({
  filters,
  courseId,
  loginActiveTab = "staff",
  curriculumProgramId,
  academicYearIds,
}) => {
  const {
    searchText,
    courses,
    familyStatus,
    adminFamilyStatus,
    lastLoggedIn,
  } = filters;
  const sendFilters = {};
  if (searchText != "") {
    sendFilters["searchText"] = searchText;
  }

  const currentFamilyStatus =
    loginActiveTab == "admin" ? adminFamilyStatus : familyStatus;

  if (loginActiveTab == "admin") {
    sendFilters["courses"] = !_.isEmpty(courses) ? courses : [];
    if (curriculumProgramId)
      sendFilters["curriculumProgramIds"] = [curriculumProgramId];
  } else {
    sendFilters["courses"] = !_.isEmpty(courseId) ? courseId : [];
  }
  if (!_.isEmpty(currentFamilyStatus) && currentFamilyStatus !== "ALL") {
    sendFilters["familyStatus"] = currentFamilyStatus;
  }

  if (!_.isEmpty(academicYearIds)) {
    sendFilters["academicYearIds"] = academicYearIds;
  }

  if (!_.isEmpty(lastLoggedIn)) {
    sendFilters["familyStatus"] = "ROSTERED";
    sendFilters["notActiveSince"] = lastLoggedIn;
  }

  return sendFilters;
};

const getFamilyFeedQueryFiltersResolver = ({
  filters,
  loginActiveTab,
  courseId,
  curriculumProgramId,
  archivalState,
  academicYearId,
  blockStatus,
}) => {
  const filterKeys = filters ? Object.keys(filters).sort().join(",") : "";
  const filterValues = filters
    ? Object.keys(filters)
        .sort()
        .map(key => {
          const value = filters[key];
          if (Array.isArray(value)) return value.join(",");
          return String(value || "");
        })
        .join("|")
    : "";

  return `${loginActiveTab || ""}|${courseId || ""}|${
    curriculumProgramId || ""
  }|${archivalState || ""}|${academicYearId || ""}|${
    blockStatus || ""
  }|${filterKeys}|${filterValues}`;
};

export const getFamilyFeedQueryFilters = _.memoize(
  params => _getFamilyFeedQueryFilters(params),
  getFamilyFeedQueryFiltersResolver
);

const getFamilyCountFilterResolver = ({
  filters,
  courseId,
  loginActiveTab,
  curriculumProgramId,
  academicYearIds,
}) => {
  const filterKeys = filters ? Object.keys(filters).sort().join(",") : "";
  const filterValues = filters
    ? Object.keys(filters)
        .sort()
        .map(key => {
          const value = filters[key];
          if (Array.isArray(value)) return value.join(",");
          return String(value || "");
        })
        .join("|")
    : "";

  const academicYearIdsStr = academicYearIds ? academicYearIds.join(",") : "";

  return `${loginActiveTab || ""}|${courseId || ""}|${
    curriculumProgramId || ""
  }|${academicYearIdsStr}|${filterKeys}|${filterValues}`;
};

export const getFamilyCountFilter = _.memoize(
  params => _getFamilyCountFilter(params),
  getFamilyCountFilterResolver
);

const _getFamiliesFilters = ({ filters, loginActiveTab = "staff" }) => {
  const { familyStatus, adminFamilyStatus, searchText, lastLoggedIn } = filters;
  const sendFilters = {};

  const currentFamilyStatus =
    loginActiveTab == "admin" ? adminFamilyStatus : familyStatus;

  if (!_.isEmpty(currentFamilyStatus) && currentFamilyStatus !== "ALL") {
    sendFilters["familyStatus"] = currentFamilyStatus;
  }
  if (searchText != "") {
    sendFilters["searchText"] = searchText;
  }

  if (!_.isEmpty(lastLoggedIn)) {
    sendFilters["familyStatus"] = "ROSTERED";
    sendFilters["notActiveSince"] = lastLoggedIn;
  }

  return sendFilters;
};

const getFamiliesFiltersResolver = ({ filters, loginActiveTab = "staff" }) => {
  const familyStatus = filters.familyStatus ?? "";
  const adminFamilyStatus = filters.adminFamilyStatus ?? "";
  const searchText = filters.searchText ?? "";
  const lastLoggedIn = filters.lastLoggedIn ?? "";

  return [
    `loginActiveTab:${loginActiveTab}`,
    `familyStatus:${familyStatus}`,
    `adminFamilyStatus:${adminFamilyStatus}`,
    `searchText:${searchText}`,
    `lastLoggedIn:${lastLoggedIn}`,
  ].join("|");
};

export const getFamiliesFilters = _.memoize(
  params => _getFamiliesFilters(params),
  getFamiliesFiltersResolver
);

const _getBulkFamilyInviteFeedQueryFilters = ({
  filters,
  courseId,
  curriculumProgramId,
}) => {
  const { searchText, familyStatus } = filters;
  const sendFilters = {};
  if (searchText != "") {
    sendFilters["searchText"] = searchText;
    sendFilters["searchTextFields"] = [
      ATTENDANCE_CONSTANTS.NAME,
      ATTENDANCE_CONSTANTS.SOURCED_ID,
      ATTENDANCE_CONSTANTS.FAMILY_MEMBER_NAME,
    ];
  }

  if (courseId) {
    sendFilters["courses"] = courseId;
  }

  if (!_.isEmpty(familyStatus) && familyStatus !== "ALL") {
    sendFilters["familyStatus"] = familyStatus;
  }

  if (curriculumProgramId) {
    sendFilters["curriculumProgramIds"] = [curriculumProgramId];
  }
  return sendFilters;
};

export const getBulkFamilyInviteFeedQueryFilters = _.memoize(
  params => _getBulkFamilyInviteFeedQueryFilters(params),
  ({ filters, courseId, curriculumProgramId }) => {
    const filterKeys = filters ? Object.keys(filters).sort().join(",") : "";
    const filterValues = filters
      ? Object.keys(filters)
          .sort()
          .map(key => {
            const value = filters[key];
            if (Array.isArray(value)) return value.join(",");
            return String(value || "");
          })
          .join("|")
      : "";

    return `${courseId || ""}|${
      curriculumProgramId || ""
    }|${filterKeys}|${filterValues}`;
  }
);

export const getStudentWithFamilyQueryFilters = ({
  filters,
  courseId,
  areAllStudentsSelected,
  selectedOrExcludedStudents,
  academicYearId,
  studentScopedFilters,
}) => {
  const { searchText } = filters;
  const sendFilters = {};

  if (searchText != "") {
    sendFilters["searchText"] = searchText;
    sendFilters["searchTextFields"] = [
      ATTENDANCE_CONSTANTS.NAME,
      ATTENDANCE_CONSTANTS.SOURCED_ID,
      ATTENDANCE_CONSTANTS.FAMILY_MEMBER_NAME,
    ];
  }

  if (courseId) {
    sendFilters["courses"] = courseId;
  }

  if (areAllStudentsSelected) {
    sendFilters["excludedStudentIds"] = selectedOrExcludedStudents;
  } else {
    sendFilters["includedStudentIds"] = selectedOrExcludedStudents;
  }

  const archivalState = _.get(studentScopedFilters, "archivalState");
  const blockStatus = _.get(studentScopedFilters, "blockStatus");
  if (archivalState) sendFilters["archivalState"] = archivalState;
  if (blockStatus) sendFilters["blockStatus"] = blockStatus;

  sendFilters["academicYearId"] = academicYearId;
  return sendFilters;
};

const REDUCER_HANDLERS = {
  [INIT_FILTERS]: (state, action) => {
    return update(state, {
      filters: { $set: initialState.filters },
    });
  },
  [UPDATE_FILTERS]: (state, action) => {
    const params = action.data;
    const keys = _.keys(params);
    _.map(keys, key => {
      state = update(state, {
        filters: {
          [key]: { $set: params[key] },
        },
      });
    });
    return state;
  },
  [INIT_FAMILY_INVITE_FILTERS]: (state, action) => {
    return update(state, {
      bulkFamilyInviteFilters: { $set: initialState.bulkFamilyInviteFilters },
    });
  },
  [UPDATE_FAMILY_INVITE_FILTERS]: (state, action) => {
    const { key, value } = action.data;
    return update(state, {
      bulkFamilyInviteFilters: { [key]: { $set: value } },
    });
  },
  [SET_SELECTED_OR_EXCLUDED_STUDENTS]: (state, action) => {
    return update(state, {
      selectedOrExcludedStudents: { $set: action.payload },
    });
  },
  [SET_ARE_ALL_STUDENTS_SELECTED]: (state, action) => {
    return update(state, {
      areAllStudentsSelected: { $set: action.payload },
    });
  },
  [SET_BULK_SELECTED_STUDENT_STATUS_COUNTS]: (state, action) => {
    return update(state, {
      bulkSelectedStudentStatusCounts: { $set: action.payload },
    });
  },
  [RESET_BULK_ACTIONS_STATE]: state => {
    return update(state, {
      selectedOrExcludedStudents: {
        $set: initialState.selectedOrExcludedStudents,
      },
      areAllStudentsSelected: { $set: initialState.areAllStudentsSelected },
      bulkSelectedStudentStatusCounts: {
        $set: _.cloneDeep(initialState.bulkSelectedStudentStatusCounts),
      },
    });
  },
  [SET_BULK_ACTIVITIES_POLLING_STATE]: (state, { payload: { isActive } }) => {
    return update(state, {
      isBulkActivitiesPollingActive: { $set: isActive },
    });
  },
};

export const initialState = {
  selectedOrExcludedStudents: [],
  areAllStudentsSelected: false,
  bulkSelectedStudentStatusCounts: {
    blocked: 0,
  },
  filters: {
    searchText: "",
    courses: [],
    familyStatus: "ALL",
    adminFamilyStatus: "ALL",
    archivalState: "ACTIVE",
    blockStatus: "ALL",
  },
  bulkFamilyInviteFilters: {
    searchText: "",
    familyStatus: ["ROSTERED", "REQUESTED", "INVITED", "UNCONNECTED"],
  },
  isBulkActivitiesPollingActive: false,
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}

export const downloadFileOfStudentListByFamilyStatus = ({
  status,
  filters,
  fileName,
}) => {
  return async dispatch => {
    try {
      const result = await client.mutate({
        mutation: downloadFileOfStudentListByFamilyStatusMutation,
        variables: {
          status,
          filters,
          fileName,
        },
      });
      if (
        _.get(result, "data.platform.generateStudentFamilyInviteReport.url")
      ) {
        const fileData = {
          name: fileName,
          url: _.get(
            result,
            "data.platform.generateStudentFamilyInviteReport.url"
          ),
          mimeType: "application/zip",
          isSuccess: true,
        };
        return fileData;
      }
      return { isSuccess: false };
    } catch (e) {
      // console.log(e);
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return { isSuccess: false };
    }
  };
};
export const familyInvitationForStudent = ({
  inviteType = "EMAIL",
  emailLanguage,
  parentsData,
  refetchQueries = [],
  includeCustomRefetchQueries = false,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const organizationId = _.get(state, "login.userInfo.org_id");
    const filters = _.get(state, "manageFamily.filters", {});
    const courseId = _.get(state, "teacher.selected_class.selected_course", "");
    const userCourseMapFilters = getUserCourseMapFilters({
      archivalState: "ALL",
    });
    const archivalStateFromFilters = _.get(filters, "archivalState", "");
    const blockStatusFromFilters = _.get(filters, "blockStatus", "");
    try {
      const result = await client.mutate({
        mutation: familyInvitationForStudentMutation,
        variables: {
          inviteType,
          emailLanguage,
          parentsData,
        },
        refetchQueries: _.size(refetchQueries)
          ? refetchQueries
          : [
              {
                query: getStudentFeedQuery,
                variables: {
                  organizationId,
                  orderBy: "FIRST_NAME",
                  orderByDirection: "ASC",
                  filters: getFamilyFeedQueryFilters({
                    filters,
                    courseId,
                    archivalState: archivalStateFromFilters,
                    blockStatus: blockStatusFromFilters,
                  }),
                  familyMembersCountFilter: getFamilyCountFilter({
                    filters,
                    courseId,
                  }),
                  familiesFilter: getFamiliesFilters({ filters }),
                  withStaffRelation: false,
                  userCourseMapFilters,
                },
              },
              ...(includeCustomRefetchQueries ? refetchQueries : []),
            ],
      });

      const warning = _.get(
        result,
        "data.platform.familyInvitationForStudent.warning",
        {}
      );

      if (!_.isEmpty(warning)) {
        return {
          isSucces: false,
          errorCode: _.get(warning, "errorCode"),
          errorData: _.get(warning, "data"),
          mutationResponse: result,
        };
      }

      return { isSuccess: true, mutationResponse: result };
    } catch (e) {
      // console.log(e);
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return { isSuccess: false };
    }
  };
};

export const onFileUpload = ({ url, curriculumProgramId }) => {
  return async (dispatch, getState) => {
    try {
      const result = await client.mutate({
        mutation: uploadFamilyMembersDetailTemplateMutation,
        variables: {
          input: {
            url,
            entity: "FAMILY_MEMBER",
            curriculumProgramId,
          },
        },
      });

      const bulkUploadData = _.get(result, "data.platform.entityBulkUpload");

      const { successData, errorData = [], uploadStatus } = bulkUploadData;
      return { successData, errorData, uploadStatus };
    } catch (error) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return { errors: true };
    }
  };
};

export const onFileCompare = ({ url, curriculumProgramId }) => {
  return async dispatch => {
    try {
      const result = await client.mutate({
        mutation: entityBulkUploadCompareDataMutation,
        variables: {
          input: {
            url,
            entity: "FAMILY_MEMBER",
            curriculumProgramId,
          },
        },
      });

      const bulkUploadCompareData = _.get(
        result,
        "data.platform.entityBulkUploadCompareData"
      );

      const { isSuccess, sheetUrl, message } = bulkUploadCompareData;
      dispatch(
        setToastMsg({
          msg: message,
          type: isSuccess ? "success" : "warning",
        })
      );
      return { isSuccess, sheetUrl };
    } catch (error) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return { errors: true };
    }
  };
};

export const confirmFamilyMemberUploadDetails = ({ uploadInfo }) => {
  const {
    added: dataToAdd,
    updated: dataToUpdate,
    deleted: dataToDelete,
    reInvited: reInvited,
    invitationLanguage,
    mapped,
  } = uploadInfo;

  return async (dispatch, getState) => {
    const organizationId = getState().login.userInfo.org_id;
    const filters = getState().manageFamily.filters;
    const courseId = getState().teacher.selected_class.selected_course;
    const userCourseMapFilters = getUserCourseMapFilters({
      archivalState: "ALL",
    });
    const archivalStateFromFilters = _.get(filters, "archivalState", "");
    const blockStatusFromFilters = _.get(filters, "blockStatus", "");

    const userArchivedFilters = {
      academicYearId: getSelectedAcademicYearId(getState()),
    };

    const automaticFamilyMemberRosterEnabled = _.get(
      mapped,
      "automaticFamilyMemberRosterEnabled",
      null
    );

    try {
      const result = await client.mutate({
        mutation: confirmFamilyMemberUploadDetailsMutation,
        variables: {
          input: {
            dataToAdd,
            dataToUpdate,
            dataToDelete,
            reInvited,
            invitationLanguage,
            ...(!_.isNil(automaticFamilyMemberRosterEnabled)
              ? { automaticFamilyMemberRosterEnabled }
              : {}),
          },
        },
        refetchQueries: [
          {
            query: getStudentFeedQuery,
            variables: {
              organizationId,
              orderBy: "FIRST_NAME",
              orderByDirection: "ASC",
              filters: getFamilyFeedQueryFilters({
                filters,
                courseId,
                archivalState: archivalStateFromFilters,
                blockStatus: blockStatusFromFilters,
              }),
              familiesFilter: getFamiliesFilters({ filters }),
              userCourseMapFilters,
              userArchivedFilters,
            },
          },
        ],
      });

      if (result) {
        const totalChanges =
          _.size(dataToAdd) + _.size(dataToDelete) + _.size(dataToUpdate);
        dispatch(
          setToastMsg({
            msg: `toastMsgs:family_member_upload_successful`,
            locale_params: [
              { key: "label", value: totalChanges, isPlainText: true },
            ],
            type: "success",
          })
        );
      } else {
        throw new Error("Unable to update");
      }

      return result;
    } catch (error) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const familyInviteForClass = ({
  emails,
  courseId,
  invitationLanguage,
  fetchAll = false,
  learningCourseId = "",
}) => {
  return async (dispatch, getState) => {
    try {
      const res = await client.mutate({
        mutation: familyInviteForClassMutation,
        variables: {
          input: {
            emails,
            courseId,
            invitationLanguage,
            fetchAll,
            learningCourseId,
          },
        },
      });

      // If no emails are passed, then it is a call for getting a public link
      return _.size(emails) === 0
        ? _.get(res, "data.platform.familyClassInvitation")
        : true;
    } catch (error) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

const getToasterMessage = (selectionStatus, data) => {
  let result;

  if (selectionStatus === "ACCEPTED") {
    if (_.size(data) === 1) {
      result = "approval_requests_accepted_with_count";
    } else {
      result = "approval_requests_accepted_with_count_plural";
    }
  } else {
    if (_.size(data) === 1) {
      result = "approval_requests_rejected_with_count";
    } else {
      result = "approval_requests_rejected_with_count_plural";
    }
  }

  return result;
};

export const decideUserApprovalStatus = ({
  selectionStatus,
  parentIdsAndCode,
  refetchQueries = [],
}) => {
  return async (dispatch, getState) => {
    const organizationId = getState().login.userInfo.org_id;
    const filters = getState().manageFamily.filters;
    const courseId = getState().teacher.selected_class.selected_course;
    const userCourseMapFilters = getUserCourseMapFilters({
      archivalState: "ALL",
    });
    const userArchivedFilters = {
      academicYearId: getSelectedAcademicYearId(getState()),
    };
    const archivalStateFromFilters = _.get(filters, "archivalState", "");
    const blockStatusFromFilters = _.get(filters, "blockStatus", "");
    try {
      await client.mutate({
        mutation: decideUserApprovalStatusMutation,
        variables: {
          input: {
            status: selectionStatus,
            parentsData: parentIdsAndCode,
            courseId,
          },
        },
        refetchQueries: _.size(refetchQueries)
          ? refetchQueries
          : [
              {
                query: getStudentFeedQuery,
                variables: {
                  organizationId,
                  orderBy: "FIRST_NAME",
                  orderByDirection: "ASC",
                  filters: getFamilyFeedQueryFilters({
                    filters,
                    courseId,
                    archivalState: archivalStateFromFilters,
                    blockStatus: blockStatusFromFilters,
                  }),
                  familiesFilter: getFamiliesFilters({ filters }),
                  userCourseMapFilters,
                  userArchivedFilters,
                },
              },
            ],
      });

      dispatch(
        setToastMsg({
          msg: `toastMsgs:${getToasterMessage(
            selectionStatus,
            parentIdsAndCode
          )}`,
          locale_params: [
            {
              key: "count",
              value: _.size(parentIdsAndCode),
              isPlainText: true,
            },
          ],
          type: "success",
        })
      );

      return true;
    } catch (error) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const removeFamilyMemberFromChild = ({
  studentId,
  familyMemberId,
  t,
  variables,
}) => {
  return async (dispatch, getState) => {
    const studentData = getStudentFamilyDetails({
      studentId,
      variables,
    });

    const families = _.get(studentData, "families", []);

    const currentFilter = _.get(variables, "familiesFilter.familyStatus", "");

    const studentFeedData = getStudentFeedForOrganizationFromCache(variables);

    const studentsFromCache = _.get(studentFeedData, "students", []);

    let updatedStudentsFeed = { node: { ...studentFeedData } };

    const totalCountFromCache = _.get(
      studentsFromCache,
      "totalCountsByFamilyStatus",
      {}
    );

    const totalConnectedFromCache = _.get(
      totalCountFromCache,
      "totalConnected",
      0
    );

    const totalFamilyMembersFromCache = _.get(
      studentFeedData,
      "familyMembers.totalCount",
      0
    );

    const totalUnconnectedFromCache = _.get(
      totalCountFromCache,
      "totalUnconnected",
      0
    );

    const totalInvitedFromCache = _.get(totalCountFromCache, "totalInvited", 0);

    if (
      _.size(families) == 1 &&
      _.includes(["INVITED", "ROSTERED"], currentFilter)
    ) {
      // As filter is there we need to remove it from the studentFeedData as family size is 1
      const studentsEdges = _.get(studentFeedData, "students.edges", []);

      const updatedStudents = _.filter(
        studentsEdges,
        item => _.get(item, "node.id", "") != studentId
      );

      updatedStudentsFeed = update(studentFeedData, {
        students: {
          edges: {
            $set: updatedStudents,
          },
          totalCountsByFamilyStatus: {
            ...(currentFilter === "ROSTERED"
              ? { totalConnected: { $set: totalConnectedFromCache - 1 } }
              : { totalInvited: { $set: totalInvitedFromCache - 1 } }),
          },
          totalUnconnected: { $set: totalUnconnectedFromCache + 1 },
        },

        familyMembers: {
          totalCount: { $set: totalFamilyMembersFromCache - 1 },
        },
      });
    } else if (_.size(families) == 1 && currentFilter === "") {
      const isInvited = _.first(families).status === "INVITED";

      updatedStudentsFeed = update(studentFeedData, {
        students: {
          totalCountsByFamilyStatus: {
            ...(!isInvited
              ? { totalConnected: { $set: totalConnectedFromCache - 1 } }
              : { totalInvited: { $set: totalInvitedFromCache - 1 } }),
          },
          totalUnconnected: { $set: totalUnconnectedFromCache + 1 },
        },

        familyMembers: {
          totalCount: { $set: totalFamilyMembersFromCache - 1 },
        },
      });
    } else {
      updatedStudentsFeed = update(studentFeedData, {
        familyMembers: {
          totalCount: { $set: totalFamilyMembersFromCache - 1 },
        },
      });
    }

    writeStudentFeedForOrganizationToCache({
      variables,
      studentFeedData: updatedStudentsFeed,
    });

    writeStudentFamiliesInCache({
      studentId,
      data: {
        ...studentData,
        families: _.filter(
          _.get(studentData, "families", []),
          item => _.get(item, "familyMember.id", "") != familyMemberId
        ),
      },
      variables,
    });

    try {
      await client.mutate({
        mutation: removeChildForFamilyMemberMutation,
        variables: {
          studentId: studentId,
          parentId: familyMemberId,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getStudentFamilyStatusInsightsQueryV2,
            variables,
          },
        ],
      });
      dispatch(
        setToastMsg({
          type: "success",
          msg: "toastMsgs:successfully_action_with_label",
          locale_params: [
            { key: "action", value: "toastMsgs:removed", isPlainText: false },
            {
              key: "label",
              value: _.lowerCase(t("common:family_member")),
              isPlainText: true,
            },
          ],
        })
      );
    } catch (e) {
      writeStudentFeedForOrganizationToCache({ variables, studentFeedData });

      writeStudentFamiliesInCache({
        studentId,
        data: studentData,
        variables,
      });

      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

const setStudentDataOnWarningOrErrorInCache = ({
  currentFilter,
  variables,
  studentFeedData,
  studentId,
  studentData,
}) => {
  if (currentFilter === "UNCONNECTED") {
    writeStudentFeedForOrganizationToCache({
      variables,
      studentFeedData,
    });
  } else if (_.includes(["INVITED", "ROSTERED", ""], currentFilter)) {
    writeStudentFamiliesInCache({
      studentId,
      data: studentData,
      variables,
    });
  }
};

export const inviteFamilyMembers = ({
  studentId,
  familyData,
  language,
  variables,
}) => {
  return async (dispatch, getState) => {
    const dataToSend = _.map(familyData, family => {
      const { firstName, middleName = "", lastName, email } = family;

      return {
        firstName,
        middleName,
        lastName,
        email,
        child: studentId,
      };
    });

    const studentData = getStudentFamilyDetails({ studentId, variables });

    const studentFeedData = getStudentFeedForOrganizationFromCache(variables);

    const currentFilter = _.get(variables, "familiesFilter.familyStatus", "");

    /* If current filter is unconnected and we add parent then we
         update cache to remove that student from unconnected list */
    if (currentFilter === "UNCONNECTED") {
      const students = _.get(studentFeedData, "students.edges", []);

      const updatedStudents = _.filter(
        students,
        item => _.get(item, "node.id", "") != studentId
      );

      const updatedStudentsFeed = update(studentFeedData, {
        students: {
          edges: {
            $set: updatedStudents,
          },
        },
      });

      writeStudentFeedForOrganizationToCache({
        variables,
        studentFeedData: updatedStudentsFeed,
      });
    } else if (_.includes(["INVITED", "ROSTERED", ""], currentFilter)) {
      const cachedFamilyData = _.get(studentData, "families", []);

      const updatedFamilies = _.map(familyData, family => {
        const {
          firstName,
          middleName = "",
          lastName,
          email,
          isCreatedByOrg,
        } = family;

        const currentTimeStamp = moment().toISOString();

        return {
          id: generateRandomId(),
          status: "INVITED",
          familyMember: {
            id: generateRandomId(),
            lastName: lastName,
            middleName: middleName,
            firstName: firstName,
            email: email,
            type: "parent",
            isCreatedByOrg,
            __typename: "FamilyMember",
          },
          __typename: "Family",
          createdAt: currentTimeStamp,
          lastUpdatedAt: currentTimeStamp,
        };
      });

      writeStudentFamiliesInCache({
        studentId,
        data: {
          ...studentData,
          families: [...cachedFamilyData, ...updatedFamilies],
        },
        variables,
      });
    }

    try {
      const result = await client.mutate({
        mutation: familyInvitationForStudentMutation,
        variables: {
          parentsData: dataToSend,
          inviteType: "EMAIL",
          emailLanguage: language,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getStudentFamilyStatusInsightsQueryV2,
            variables,
          },
        ],
        update: (cache, data) => {
          const response = _.get(
            data,
            "data.platform.familyInvitationForStudent.response",
            []
          );
          //updating the cache with result from server as we need updated familyId
          const studentFamilyDetails = getStudentFamilyDetails({
            studentId,
            variables,
          });
          const cachedFamilyData = _.get(studentFamilyDetails, "families", []);

          // write with id from db once mutation is successful as we need id in case user removes the member added
          const updatedFamilies = _.map(cachedFamilyData, family => {
            const newlyInvited = _.find(response, responseFamily => {
              return responseFamily.email === family.familyMember.email;
            });

            if (newlyInvited) {
              return {
                ...family,
                familyMember: {
                  ...family.familyMember,
                  id: newlyInvited.id,
                },
              };
            } else {
              return family;
            }
          });
          writeStudentFamiliesInCache({
            studentId,
            data: {
              ...studentData,
              families: updatedFamilies,
            },
            variables,
          });
        },
      });

      const warning = _.get(
        result,
        "data.platform.familyInvitationForStudent.warning",
        {}
      );
      const errorCode = _.get(warning, "errorCode");
      const isParentInOtherRegion = errorCode === "PARENT_IN_OTHER_REGION";

      if (!_.isEmpty(warning)) {
        if (isParentInOtherRegion) {
          setStudentDataOnWarningOrErrorInCache({
            currentFilter,
            variables,
            studentFeedData,
            studentId,
            studentData,
          });
        }

        return {
          isSuccess: false,
          errorCode: _.get(warning, "errorCode"),
          errorData: _.get(warning, "data"),
          mutationResponse: result,
        };
      }

      return { isSuccess: true, mutationResponse: result };
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));

      setStudentDataOnWarningOrErrorInCache({
        currentFilter,
        variables,
        studentFeedData,
        studentId,
        studentData,
      });

      return { isSuccess: false };
    }
  };
};
